import {useMemo} from 'react';
import {Link as RouterLink, useNavigate} from 'react-router-dom';
import {Autocomplete, Box, Button, Chip, Link, TextField} from '@mui/material';
import {MRT_ColumnDef} from 'material-react-table';

import {Banner, useGetBanners} from '@entities/banner';
import {GeoFilterOption, useGetGeoFilterOptions} from '@entities/common';
import {MrTable} from '@shared/components';
import {FilterRow, SelectFilter} from '@shared/components/filters';
import {
  BANNER_STATUS_FILTER,
  DATETIME_FORMAT,
  entityTableCommonProps,
  ROUTE__BANNER_CREATE,
  ROUTE__BANNER_EDIT,
} from '@shared/constants';
import {BaseComponentLayout} from '@shared/layouts';
import {formatInTz} from '@shared/utils';

import {useUrlState} from '@/shared/hooks';

type UrlState = {
  geoFilterIds: string[];
  isActive: boolean | null;
};

const getColumns = (geoOptions: GeoFilterOption[]): MRT_ColumnDef<Banner>[] => [
  {
    accessorKey: 'name',
    header: 'Name',
    Cell: ({row}) => (
      <Link
        component={RouterLink}
        to={ROUTE__BANNER_EDIT.replace(':id', row.original.id)}
      >
        {row.original.name || row.original.id}
      </Link>
    ),
  },
  {
    accessorKey: 'order',
    header: 'Order',
    size: 80,
  },
  {
    accessorKey: 'activeFrom',
    header: 'Active from',
    size: 160,
    Cell: ({row}) => {
      const date = row.original.activeFrom;
      return date ? formatInTz(date, DATETIME_FORMAT) : '—';
    },
  },
  {
    accessorKey: 'activeTo',
    header: 'Active to',
    size: 160,
    Cell: ({row}) => {
      const date = row.original.activeTo;
      return date ? formatInTz(date, DATETIME_FORMAT) : '—';
    },
  },
  {
    accessorKey: 'geos',
    header: 'GEO tags',
    size: 220,
    enableSorting: false,
    Cell: ({row}) => {
      const geos = row.original.geos;

      if (geos.length === 0) {
        return '—';
      }

      return (
        <Box display="flex" flexWrap="wrap" gap={0.5}>
          {geos.map(id => {
            const option = geoOptions.find(opt => opt.id === id);
            return <Chip key={id} size="small" label={option?.name || id} />;
          })}
        </Box>
      );
    },
  },
];

export const BannersPage = () => {
  const navigate = useNavigate();
  const {urlState, setUrl} = useUrlState<UrlState>({
    geoFilterIds: [],
    isActive: null,
  });
  const {data, isPending, error} = useGetBanners({
    geos: urlState.geoFilterIds.length > 0 ? urlState.geoFilterIds : undefined,
    onlyActive: urlState.isActive ?? undefined,
  });
  const {data: geoOptions = []} = useGetGeoFilterOptions();
  const columns = useMemo(() => getColumns(geoOptions), [geoOptions]);

  return (
    <BaseComponentLayout isPending={isPending} error={error}>
      <MrTable
        columns={columns}
        data={data ?? []}
        renderTopToolbarCustomActions={() => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            gap={2}
          >
            <Button onClick={() => navigate(ROUTE__BANNER_CREATE)}>
              Create banner
            </Button>
            <FilterRow>
              <Autocomplete
                multiple
                size="small"
                options={geoOptions}
                value={geoOptions.filter(option =>
                  urlState.geoFilterIds.includes(option.id),
                )}
                getOptionLabel={option => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, selected) =>
                  setUrl(prev => ({
                    ...prev,
                    geoFilterIds: selected.map(option => option.id),
                  }))
                }
                renderInput={params => (
                  <TextField {...params} placeholder="GEO tags" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({index})}
                      key={option.id}
                      label={option.name}
                      size="small"
                    />
                  ))
                }
                sx={{minWidth: 200}}
                disableCloseOnSelect
              />

              <SelectFilter
                value={urlState.isActive}
                onChange={isActive =>
                  setUrl(prev => ({
                    ...prev,
                    isActive,
                  }))
                }
                options={BANNER_STATUS_FILTER.options}
              />
            </FilterRow>
          </Box>
        )}
        {...entityTableCommonProps}
        enableRowActions={false}
      />
    </BaseComponentLayout>
  );
};
